/**
 * Utility functions for detecting monochrome images
 * Used to conditionally display a colorpicker for single-color images (including PNG with transparency)
 */

export interface MonochromeDetectionResult {
  isMonochrome: boolean;
  detectedColor: string | null;
  hasTransparency: boolean;
}

/**
 * Converts RGB values to hex color string
 */
const rgbToHex = (r: number, g: number, b: number): string => {
  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * Checks if two colors are similar within a tolerance (for compressed PNG variations)
 */
const colorsAreSimilar = (
  r1: number, g1: number, b1: number,
  r2: number, g2: number, b2: number,
  tolerance: number = 10,
): boolean => (
  Math.abs(r1 - r2) <= tolerance
    && Math.abs(g1 - g2) <= tolerance
    && Math.abs(b1 - b2) <= tolerance
);

/**
 * Detects if an image is monochrome (single color + optional transparency)
 * @param imageUrl - URL of the image to analyze
 * @param colorTolerance - Tolerance for color variation (default 10, for compressed images)
 * @param transparencyThreshold - Alpha value below which a pixel is considered transparent (default 10)
 * @returns Promise with detection result
 */
export const detectMonochromeImage = (
  imageUrl: string,
  colorTolerance: number = 30,
  transparencyThreshold: number = 100,
): Promise<MonochromeDetectionResult> => new Promise((resolve) => {
  // Default result for errors or non-analyzable images
  const defaultResult: MonochromeDetectionResult = {
    isMonochrome: false,
    detectedColor: null,
    hasTransparency: false,
  };

  // Skip placeholder images
  if (imageUrl.includes('placeholder') || !imageUrl) {
    resolve(defaultResult);
    return;
  }

  const img = new Image();
  img.crossOrigin = 'anonymous';

  img.onload = () => {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        resolve(defaultResult);
        return;
      }

      // Limit canvas size for performance (sample the image)
      const maxSize = 100;
      const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
      canvas.width = Math.floor(img.width * scale);
      canvas.height = Math.floor(img.height * scale);

      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const { data } = imageData;

      let firstOpaqueColor: { r: number; g: number; b: number } | null = null;
      let hasTransparency = false;
      let coloredPixelCount = 0;

      // Analyze pixels
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        const a = data[i + 3];

        // Check for transparency
        if (a < transparencyThreshold) {
          hasTransparency = true;
          continue;
        }

        coloredPixelCount += 1;

        // First colored pixel sets the reference color
        if (!firstOpaqueColor) {
          firstOpaqueColor = { r, g, b };
          continue;
        }

        // Check if current pixel matches the reference color
        if (!colorsAreSimilar(r, g, b, firstOpaqueColor.r, firstOpaqueColor.g, firstOpaqueColor.b, colorTolerance)) {
          // Found a different color - not monochrome
          resolve(defaultResult);
          return;
        }
      }

      // Need at least some colored pixels to determine monochrome
      if (!firstOpaqueColor || coloredPixelCount < 10) {
        resolve(defaultResult);
        return;
      }

      // Image is monochrome
      resolve({
        isMonochrome: true,
        detectedColor: rgbToHex(firstOpaqueColor.r, firstOpaqueColor.g, firstOpaqueColor.b),
        hasTransparency,
      });
    } catch (error) {
      // Canvas security error (CORS) or other issues
      console.warn('[MonochromeDetection] Error analyzing image:', error);
      resolve(defaultResult);
    }
  };

  img.onerror = () => {
    console.warn('[MonochromeDetection] Failed to load image:', imageUrl);
    resolve(defaultResult);
  };

  // Add timestamp to bypass cache issues with CORS
  const separator = imageUrl.includes('?') ? '&' : '?';
  img.src = `${imageUrl}${separator}_t=${Date.now()}`;
});

/**
 * Cache for monochrome detection results to avoid re-analyzing the same images
 */
const detectionCache = new Map<string, MonochromeDetectionResult>();

/**
 * Detects if an image is monochrome with caching
 * @param imageUrl - URL of the image to analyze
 * @returns Promise with detection result
 */
export const detectMonochromeImageCached = async (imageUrl: string): Promise<MonochromeDetectionResult> => {
  // Check cache first
  if (detectionCache.has(imageUrl)) {
    return detectionCache.get(imageUrl)!;
  }

  const result = await detectMonochromeImage(imageUrl);
  detectionCache.set(imageUrl, result);

  return result;
};

/**
 * Clears the detection cache (useful when images are modified)
 */
export const clearMonochromeDetectionCache = (imageUrl?: string): void => {
  if (imageUrl) {
    detectionCache.delete(imageUrl);
  } else {
    detectionCache.clear();
  }
};

// ============================================================================
// Image Colorization URL Utilities
// ============================================================================

const MEDIA_DOMAIN = 'media.shopimind.io';

/**
 * Patterns for colorizable image paths
 * - /clients/shared_library/{category}/[subdirs...]/file → /clri/csl-{category}/{color}/[subdirs...]/file
 * - /clients/shopFiles/{shopId}/[subdirs...]/file → /clri/c-{shopId}/{color}/[subdirs...]/file
 */
const COLORIZABLE_PATTERNS = {
  sharedLibrary: /^\/clients\/shared_library\/([^/]+)\/(.+)$/,
  shopFiles: /^\/clients\/shopFiles\/([^/]+)\/(.+)$/,
};

/**
 * Pattern to detect already colorized URLs
 * /clri/{prefix}-{param}/{color}/[path...]/file
 */
const COLORIZED_URL_PATTERN = /^\/clri\/(csl|c)-([^/]+)\/([a-fA-F0-9]{6})\/(.+)$/;

export interface ColorizedUrlInfo {
  isColorizable: boolean;
  isAlreadyColorized: boolean;
  originalUrl: string | null;
  currentColor: string | null;
}

/**
 * Checks if a URL is from the media domain and can be colorized
 */
export const isColorizableUrl = (url: string): boolean => {
  try {
    const urlObj = new URL(url);
    if (urlObj.hostname !== MEDIA_DOMAIN) {
      return false;
    }
    const pathname = urlObj.pathname;
    return COLORIZABLE_PATTERNS.sharedLibrary.test(pathname)
      || COLORIZABLE_PATTERNS.shopFiles.test(pathname)
      || COLORIZED_URL_PATTERN.test(pathname);
  } catch {
    return false;
  }
};

/**
 * Analyzes a URL and returns colorization info
 */
export const getColorizedUrlInfo = (url: string): ColorizedUrlInfo => {
  const defaultResult: ColorizedUrlInfo = {
    isColorizable: false,
    isAlreadyColorized: false,
    originalUrl: null,
    currentColor: null,
  };

  try {
    const urlObj = new URL(url);
    if (urlObj.hostname !== MEDIA_DOMAIN) {
      return defaultResult;
    }

    const pathname = urlObj.pathname;

    // Check if already colorized
    const colorizedMatch = pathname.match(COLORIZED_URL_PATTERN);
    if (colorizedMatch) {
      const [, prefix, param, color, rest] = colorizedMatch;
      let originalPath: string;
      if (prefix === 'csl') {
        originalPath = `/clients/shared_library/${param}/${rest}`;
      } else {
        originalPath = `/clients/shopFiles/${param}/${rest}`;
      }
      return {
        isColorizable: true,
        isAlreadyColorized: true,
        originalUrl: `https://${MEDIA_DOMAIN}${originalPath}`,
        currentColor: `#${color}`,
      };
    }

    // Check if colorizable (original URL)
    if (COLORIZABLE_PATTERNS.sharedLibrary.test(pathname) || COLORIZABLE_PATTERNS.shopFiles.test(pathname)) {
      return {
        isColorizable: true,
        isAlreadyColorized: false,
        originalUrl: url,
        currentColor: null,
      };
    }

    return defaultResult;
  } catch {
    return defaultResult;
  }
};

/**
 * Generates a colorized URL from an original or already colorized URL
 * @param url - Original or colorized image URL
 * @param color - Hex color (with or without #)
 * @returns Colorized URL or null if URL is not colorizable
 */
export const getColorizedUrl = (url: string, color: string): string | null => {
  const colorHex = color.replace('#', '').toLowerCase();
  if (!/^[a-fA-F0-9]{6}$/.test(colorHex)) {
    console.warn('[Colorization] Invalid color format:', color);
    return null;
  }

  try {
    const urlObj = new URL(url);
    if (urlObj.hostname !== MEDIA_DOMAIN) {
      return null;
    }

    let pathname = urlObj.pathname;

    // If already colorized, extract original path first
    const colorizedMatch = pathname.match(COLORIZED_URL_PATTERN);
    if (colorizedMatch) {
      const [, prefix, param, , rest] = colorizedMatch;
      if (prefix === 'csl') {
        pathname = `/clients/shared_library/${param}/${rest}`;
      } else {
        pathname = `/clients/shopFiles/${param}/${rest}`;
      }
    }

    // Match shared_library pattern
    const sharedLibMatch = pathname.match(COLORIZABLE_PATTERNS.sharedLibrary);
    if (sharedLibMatch) {
      const [, category, rest] = sharedLibMatch;
      return `https://${MEDIA_DOMAIN}/clri/csl-${category}/${colorHex}/${rest}`;
    }

    // Match shopFiles pattern
    const shopFilesMatch = pathname.match(COLORIZABLE_PATTERNS.shopFiles);
    if (shopFilesMatch) {
      const [, shopId, rest] = shopFilesMatch;
      return `https://${MEDIA_DOMAIN}/clri/c-${shopId}/${colorHex}/${rest}`;
    }

    return null;
  } catch {
    return null;
  }
};

/**
 * Extracts the original (non-colorized) URL from a potentially colorized URL
 * @param url - Image URL (colorized or original)
 * @returns Original URL
 */
export const getOriginalUrl = (url: string): string => {
  const info = getColorizedUrlInfo(url);
  return info.originalUrl || url;
};
